import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Select from 'react-select';

import { getAllZones } from '../../api/master';
import { getAllEmployees } from '../../api/employee';
import { useAuth } from '../../context/AuthContext';
import { useWorkCalendar } from '../../hooks/useWorkCalendar';

import EmployeeMain from '../../types/employee';
import { City } from '../../types/tourPlan';
import { ChevronLeft, ChevronRight } from "lucide-react";


/* ---------------- TYPES ---------------- */

interface Zone {
  id: number;
  name: string;
}

export interface TourPlanRequest {
  tenant_id: number;
  employee_id: number;
  location_id: number;
  tour_plan_date: string;
  status: 'submitted';
}

interface AddTourPlanModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TourPlanRequest[]) => void;
}

/* ---------------- COMPONENT ---------------- */

const AddTourPlanModal: React.FC<AddTourPlanModalProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
}) => {
  const { user } = useAuth();
  const { fetchLocations } = useWorkCalendar();

  const [currentStep, setCurrentStep] = useState(1);

  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState('');

  const [allEmployees, setAllEmployees] = useState<EmployeeMain[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<EmployeeMain[]>([]);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  const [locations, setLocations] = useState<City[]>([]);
  const [dateLocationMap, setDateLocationMap] =
    useState<Record<string, City | null>>({});

  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());

  /* ---------------- FETCH DATA ---------------- */

  useEffect(() => {
    if (!user?.tenant_id) return;

    getAllZones(user.tenant_id).then(setZones);
    getAllEmployees(user.tenant_id).then(setAllEmployees);
  }, [user]);

  useEffect(() => {
    if (!selectedZone) {
      setFilteredEmployees([]);
      return;
    }

    const zoneId = Number(selectedZone);
    const filtered = allEmployees.filter(emp => emp.zone_id === zoneId);
    setFilteredEmployees(filtered);

    setSelectedEmployeeId(null);
    setLocations([]);
    setDateLocationMap({});
  }, [selectedZone, allEmployees]);

  /* ---------------- OPTIONS ---------------- */

  const employeeOptions = filteredEmployees.map(emp => ({
    value: emp.id,
    label: emp.user.name,
  }));

  const locationOptions = locations.map(loc => ({
    value: loc.id,
    label: loc.name,
  }));

  /* ---------------- DATE UTILS ---------------- */

  const getDatesForMonth = () => {
    if (!month || !year) return [];

    const monthIndex = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ].indexOf(month);

    const daysInMonth = new Date(Number(year), monthIndex + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dateObj = new Date(Number(year), monthIndex, day);

      // 0 = Sunday
      if (dateObj.getDay() === 0) return null;

      return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }).filter(Boolean) as string[];

  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedEmployeeId) return;

    const payload: TourPlanRequest[] = [];

    Object.entries(dateLocationMap).forEach(([date, loc]) => {
      if (!loc) return;

      payload.push({
        tenant_id: user.tenant_id,
        employee_id: selectedEmployeeId,
        location_id: loc.id,
        tour_plan_date: date,
        status: 'submitted',
      });
    });


    console.log(payload)
    onSubmit(payload);
    resetForm();
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedZone('');
    setSelectedEmployeeId(null);
    setLocations([]);
    setDateLocationMap({});
    setMonth('');
    setYear(new Date().getFullYear().toString());
  };

  if (!isOpen) return null;

  const isStep1Valid = selectedZone && selectedEmployeeId && month && year;
  const isStep2Valid = Object.values(dateLocationMap).some(v => v !== null);

  /* ---------------- UI ---------------- */

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[100vh] overflow-hidden">

        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Add Tour Plan</h2>
          <button onClick={() => onOpenChange(false)}>
            <X />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[65vh]">

          {currentStep === 1 && (
            <div className="grid grid-cols-2 gap-4">
              <select
                value={selectedZone}
                onChange={e => setSelectedZone(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="">Select Zone</option>
                {zones.map(z => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>

              <Select
                isDisabled={!selectedZone}
                options={employeeOptions}
                value={employeeOptions.find(o => o.value === selectedEmployeeId) || null}
                onChange={opt => {
                  const id = opt?.value ?? null;
                  setSelectedEmployeeId(id);
                  id
                    ? fetchLocations(id).then(setLocations)
                    : setLocations([]);
                }}
                placeholder="Select employee"
              />

              <select
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="">Select Month</option>
                {[
                  'January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December',
                ].map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>

              <select
                value={year}
                onChange={e => setYear(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="2026">2026</option>
              </select>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              {getDatesForMonth().map(date => (
                <div key={date} className="border rounded-lg p-3">
                  <div className="font-medium mb-2">{date}</div>

                  <Select
                    options={locationOptions}
                    value={
                      dateLocationMap[date]
                        ? {
                          value: dateLocationMap[date]!.id,
                          label: dateLocationMap[date]!.name,
                        }
                        : null
                    }
                    onChange={opt =>
                      setDateLocationMap(prev => ({
                        ...prev,
                        [date]: opt
                          ? ({ id: opt.value, name: opt.label } as City)
                          : null,
                      }))
                    }
                    placeholder="Select location"
                    isClearable
                  />

                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-between p-6 border-t">
          <button
            disabled={currentStep === 1}
            onClick={() => setCurrentStep((s) => s - 1)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all
      ${currentStep === 1
                ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-70"
                : "bg-gray-200 text-gray-900 cursor-pointer hover:bg-gray-300 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-gray-400"
              }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          {currentStep < 2 ? (
            <button
              disabled={!isStep1Valid}
              onClick={() => setCurrentStep(2)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold transition-all
        ${!isStep1Valid
                  ? "bg-blue-700 text-white opacity-60 cursor-not-allowed"
                  : "bg-blue-700 text-white cursor-pointer hover:bg-blue-800 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-400"
                }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              disabled={!isStep2Valid}
              onClick={handleSubmit}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold transition-all
        ${!isStep2Valid
                  ? "bg-blue-700 text-white opacity-60 cursor-not-allowed"
                  : "bg-blue-700 text-white cursor-pointer hover:bg-blue-800 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-400"
                }`}
            >
              Submit Tour Plan
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default AddTourPlanModal;
